import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  format,
  compareDesc,
  formatDistanceToNow,
  isValid,
  differenceInCalendarDays,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fromJS } from "immutable";
import { formatAmount } from "../api/DataTypeUtils";
import {
  ChevronRightIcon,
  PlusIcon,
  PencilSquareIcon,
  InboxIcon,
  FolderOpenIcon,
} from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ActivityTabs } from "./ItemActivityLogs";
import {
  CheckSquareIcon,
  MailPlus,
  Zap,
  CalendarCheck,
  CalendarClock,
  Activity,
  CornerDownRight,
  Kanban,
  BadgePercent,
  BadgeDollarSign,
  CalendarDays,
  Phone,
  Flag,
  Users,
} from "lucide-react";
import ActivityAction from "../actions/ActivityAction";
import { ActivityTypes, DialogTypes } from "../constants/action-types";
import { EmailRowComponent, EmailRow } from "../main/item-view/Right/EmailsSection";
import { FileRowComponent, FileRow } from "../main/item-view/Right/FilesSection";
import DefaultAvatar from "../components/DefaultAvatar";
import { deleteItemLog, downloadFileLogActivity, markAsDoneActivity } from "../api/ProjectApi";
import { openLink } from "../api/AppLinkApi";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import QuickNotesRow from "./components/QuickNotesRow";
import QuickTaskRow from "./components/QuickTaskRow";
import { getBaseUrl, isGmailContext } from "../api/api";
import ConnectionCard, { hexToRgba } from "./components/ConnectionCard";
import { buildTimeValues } from "./CalendarTimeline";
import ChartLoading from "../insight/components/ChartLoading";
import TaskAction from "../actions/TaskAction";
import {
  ActivityActor,
  ActivityViewRowContent,
  formatDistanceToNowActivity,
  groupSameDay,
} from "./ActivityView";
import { navigateTo } from "../Navigator";

interface FunnelStageCardProps {
  currentStage?: number;
  stageName?: string;
  totalStages?: number;
  stages?: any[]; // Array of stage objects with name
  onSelectStage?: (bucketId: string) => void;
}

const FunnelStageCard: React.FC<FunnelStageCardProps> = ({
  currentStage = 2,
  stageName = "Qualification",
  totalStages = 6,
  stages = [],
  onSelectStage,
}) => {
  const { t } = useTranslation();
  const [hoveredStage, setHoveredStage] = React.useState<number | null>(null);

  const getStageName = (stageIndex: number): string => {
    if (stages && stages.length > 0 && stages[stageIndex - 1]) {
      const s = stages[stageIndex - 1];
      return s.get?.("name") || s.name || "";
    }
    return stageName;
  };
  const getStageId = (stageIndex: number): string | undefined => {
    if (stages && stages.length > 0 && stages[stageIndex - 1]) {
      const s = stages[stageIndex - 1];
      return s.get?.("id") || s.id;
    }
    return undefined;
  };

  const displayStageName = hoveredStage ? getStageName(hoveredStage) : stageName;

  return (
    <div className="rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1">
          <Kanban className="w-4 h-4 text-gray-600" />
          <div className="text-xs font-medium text-gray-600 leading-4">{t("Funnel Stage")}</div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-[2px] items-center w-full">
            {Array.from({ length: totalStages }).map((_, index) => {
              const stageIndex = index + 1;
              const isActive = stageIndex === currentStage;
              const isCompleted = stageIndex < currentStage;

              return (
                <div
                  key={index}
                  className={`h-6 flex-1 min-w-0 flex items-end cursor-pointer transition-opacity ${
                    hoveredStage === stageIndex ? "opacity-80" : ""
                  }`}
                  onMouseEnter={() => setHoveredStage(stageIndex)}
                  onMouseLeave={() => setHoveredStage(null)}
                  title={`Stage ${stageIndex}: ${getStageName(stageIndex)}`}
                  aria-label={`Stage ${stageIndex}: ${getStageName(stageIndex)}`}
                >
                  <div
                    className={`w-full rounded-[10px] ${
                      isActive
                        ? "h-[5px] bg-green-700"
                        : isCompleted
                          ? "h-[3px] bg-green-300"
                          : "h-[3px] bg-gray-300"
                    }`}
                    onClick={() => {
                      const id = getStageId(stageIndex);
                      if (id && onSelectStage) onSelectStage(String(id));
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        const id = getStageId(stageIndex);
                        if (id && onSelectStage) onSelectStage(String(id));
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className="text-sm font-medium text-gray-900 leading-5">{displayStageName}</div>
        </div>
      </div>
    </div>
  );
};

interface ItemOverviewProps {
  itemActivities: any;
  allActivities: any;
  itemLinks: any;
  item?: any;
  hasGmail?: boolean;
  hasOutlook?: boolean;
  onSelectStage?: (bucketId: string) => void;
  onNavigateTab?: (key: string) => void;
  isLoading?: boolean; // Loading state for itemLogs
}

const ItemOverview: React.FC<ItemOverviewProps> = ({
  itemActivities,
  allActivities,
  itemLinks,
  item = fromJS({}),
  hasGmail,
  hasOutlook,
  onSelectStage,
  onNavigateTab,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const upcomingFilters =
    useSelector((state: any) => state.activityReducer.get("upcomingFilters")) || fromJS({});
  const projectId = useSelector((state: any) => state.projectReducer.getIn(["project", "id"]));
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  // Get funnel stage data similar to SaleFunnelNav
  const contentType = item.get("type") || "SALE";
  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType));
  const bucketsLoading =
    useSelector((state: any) => state.bucketReducer.getIn([contentType, "loading"])) || false;
  const bucketId = item.getIn(["data", "bucketId", "value"]);
  // Get loading states from Redux (similar to DealDetailPanel)
  const taskLoading =
    useSelector((state: any) => state.taskReducer.getIn([contentType, "loading"])) || false;

  const itemId = item.get("id");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleteType, setDeleteType] = useState<"task" | "file" | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getMember = (members, accountId) => {
    return (
      members.find((member) => member.get("accountId") == accountId) ||
      fromJS({ accountId, displayName: accountId })
    );
  };

  const byActivityType = (atypes: string[]) =>
    itemActivities
      .filter((l) => atypes.includes(l.getIn(["event", "data", "activityType"])))
      .toArray()
      .sort((a: any, b: any) =>
        compareDesc(
          new Date(a.getIn(["event", "createdAt"]) * 1000),
          new Date(b.getIn(["event", "createdAt"]) * 1000)
        )
      )
      .slice(0, 3);

  const recentActivity = allActivities.filter(
    (item) =>
      item.getIn(["event", "data", "emailStatus"]) !== "DRAFT" &&
      !(item.getIn(["event", "action"]) === "edited" && !item.getIn(["event", "fieldId"]))
  );
  const recentTasks = byActivityType([ActivityTypes.task]);
  const recentNotes = byActivityType([ActivityTypes.commented]);
  const recentEmails = byActivityType([
    ActivityTypes.sendEmail,
    ActivityTypes.sendDraftEmail,
    ActivityTypes.sendScheduledEmail,
    ActivityTypes.recordSentEmail,
  ]);
  const recentFiles = byActivityType([ActivityTypes.file]);

  // Get funnel stage data similar to SaleFunnelNav
  const getFunnelStage = () => {
    if (!buckets || buckets.size === 0) {
      return { currentStage: 1, stageName: "", totalStages: 0 };
    }

    // Find current bucket index
    let currentStageIndex = 0;
    let currentStageName = "";

    if (bucketId) {
      buckets.forEach((bucket: any, index: number) => {
        if (bucket.get("id") === bucketId) {
          currentStageIndex = index + 1;
          currentStageName = bucket.get("name") || "";
        }
      });
    }

    // If no bucketId found, default to first stage
    if (currentStageIndex === 0 && buckets.size > 0) {
      currentStageIndex = 1;
      currentStageName = buckets.get(0)?.get("name") || "";
    }

    // Convert buckets to stages array
    const stagesArray = buckets.toArray();

    return {
      currentStage: currentStageIndex,
      stageName: currentStageName,
      totalStages: buckets.size,
      stages: stagesArray,
    };
  };

  const funnelStage = getFunnelStage();

  // Scoring and metrics
  const scoring = useSelector((state: any) => state.scoring);
  const connectionStrength =
    scoring?.get("connectionStrength") ||
    fromJS({ strength: 0, text: "No connection", color: "#838282", version: "0" });
  const connectionsCount = item.getIn(["data", "totalActivities", "value"]) || 0;

  const predictPercent = item.getIn(["data", "estimatedProbability", "value"]) || 0;

  // Amount and currency
  const currency = item.getIn(["data", "currency", "value"]) || "USD";
  const estimatedRevenue = item.getIn(["data", "estimatedRevenue", "value"]) || 0;
  const estimatedArr = item.getIn(["data", "estimatedArr", "value"]) || 0;
  const fundingRaised = item.getIn(["data", "fundingRaised", "value"]) || 0;
  const employeeRange = item.getIn(["data", "employeeRange", "value"]) || "";

  const getObjectData = (link: any) => {
    const {
      sourceObject = {},
      sourceObjectType = "",
      targetObject = {},
      targetObjectType = "",
    } = link;
    if (sourceObject?.id === itemId) {
      return {
        linkId: link.id,
        objectData: targetObject,
        objectType: targetObjectType,
      };
    }
    return {
      linkId: link.id,
      objectData: sourceObject,
      objectType: sourceObjectType,
    };
  };

  const getTeamMembers = () => {
    if (contentType !== "COMPANY") return [];
    return itemLinks
      .toJS()
      .map(getObjectData)
      .filter((data) => data.objectType === "CONTACT")
      .map((data) => {
        const itemData = fromJS(data.objectData.data);
        return {
          id: data.linkId,
          name: itemData.getIn(["name", "value"]),
          avatarUrl: data.objectData.iconUrl,
        };
      });
  };

  const teamMembers = getTeamMembers();

  const renderIconComposeEmail = () => {
    if (hasGmail) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (hasOutlook) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const upcomingOfType = (type: string) => {
    const timelines = fromJS(buildTimeValues()).sortBy((item) => item.get("distanceToNow"));
    const sortedTimelines = timelines.filter((item) => item.get("distanceToNow") >= 0);
    const truncateDate = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };
    const items = itemActivities
      .filter((l: any) => l.getIn(["event", "data", "activityType"]) === type)
      .toArray()
      .map((l: any) => {
        const timeZone = l.getIn(["event", "data", "timeZone"]);
        let start = l.getIn(["event", "data", "start"]);
        if (typeof start === "number") {
          start = formatInTimeZone(
            start,
            timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "yyyy-MM-dd"
          );
        }
        let startTime = l.getIn(["event", "data", "startTime"]);
        const startDateTime = new Date(`${start}T${startTime}`);
        if (!isValid(startDateTime)) {
          startTime = sortedTimelines.getIn([0, "value"]);
        }
        let end = l.getIn(["event", "data", "end"]);
        if (typeof end === "number") {
          end = formatInTimeZone(
            end,
            timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "yyyy-MM-dd"
          );
        }
        let endTime = l.getIn(["event", "data", "endTime"]);
        const endDateTime = new Date(`${end}T${endTime}`);
        if (!isValid(endDateTime)) {
          endTime = sortedTimelines.getIn([2, "value"]);
        }
        return {
          log: l,
          start,
          startTime,
          end,
          endTime,
          summary: l.getIn(["event", "data", "summary"]),
        };
      })
      .filter((x) => {
        if (type === "task") {
          return truncateDate(new Date(x.start)) >= truncateDate(new Date());
        }
        if (type === "meeting") {
          let startDateTime = new Date(`${x.start}T${x.startTime}`);
          if (!isValid(startDateTime)) {
            startDateTime = new Date(x.start);
          }
          return startDateTime > new Date();
        }
        return false;
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    return items[0];
  };

  const upcomingTask = upcomingOfType("task");
  const upcomingMeeting = upcomingOfType("meeting");

  const computeDueLabelTask = () => {
    if (!upcomingTask) return "";
    const startDate = new Date(upcomingTask.start);
    if (!isValid(startDate)) return "";
    const days = differenceInCalendarDays(startDate, new Date());
    if (days === 0) return "Due today";
    if (days > 0) return `Due in ${days} day${days > 1 ? "s" : ""}`;
    const ago = Math.abs(days);
    return `Due ${ago} day${ago > 1 ? "s" : ""} ago`;
  };
  const nextDueTaskLabel = computeDueLabelTask();

  const computeNextMeetingLabel = () => {
    if (!upcomingMeeting) return "";
    const startDate = new Date(`${upcomingMeeting.start}T${upcomingMeeting.startTime}`);
    const endDate = new Date(`${upcomingMeeting.end}T${upcomingMeeting.endTime}`);
    if (!isValid(startDate)) return "";
    const startDateStr = format(startDate, "hh:mm a");
    const endDateStr = endDate ? format(endDate, "hh:mm a") : "";
    if (endDateStr && startDateStr !== endDateStr) {
      return `${startDateStr} - ${endDateStr} ${t("on")} ${format(startDate, "dd MMMM yyyy")}`;
    }
    return `${startDateStr} ${t("on")} ${format(startDate, "dd MMMM yyyy")}`;
  };
  const nextMeetingLabel = computeNextMeetingLabel();

  const lastInteraction = itemActivities
    .toArray()
    .sort(
      (a: any, b: any) =>
        (b.getIn(["event", "createdAt"]) || 0) - (a.getIn(["event", "createdAt"]) || 0)
    )[0];

  const lastInteractionText = (() => {
    if (!lastInteraction) return "";
    const ts = lastInteraction.getIn(["event", "createdAt"]);
    if (!ts) return "";
    const d = new Date(ts * 1000);
    if (!isValid(d)) return "";
    return formatDistanceToNow(d, { addSuffix: true });
  })();

  const QuickSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    action: React.ReactNode;
    items: any[];
    variant: "activity" | "tasks" | "notes" | "emails" | "files";
    onNavigateTab?: (key: string) => void;
  }> = ({ title, icon, action, items, variant, onNavigateTab }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const mapTitleToTabKey = (ttl: string) => {
      const k = ttl.toLowerCase();
      if (k.includes("activity")) return ActivityTabs.ACTIVITY.key;
      if (k.includes("task")) return ActivityTabs.TASKS.key;
      if (k.includes("note")) return ActivityTabs.NOTE.key;
      if (k.includes("email")) return ActivityTabs.EMAIL.key;
      if (k.includes("file")) return ActivityTabs.FILE.key;
      return "";
    };

    const renderActivity = () => {
      const listGroup = groupSameDay(items);
      return listGroup
        .entrySeq()
        .take(3)
        .map(([key, activities]) => {
          if (activities.size === 0) return null;
          const lastActivity = activities.first();
          const timeLabel = formatDistanceToNowActivity(lastActivity);
          return (
            <div key={key} className="flex items-start w-full h-full px-2">
              <div className="w-[120px] flex flex-col items-start shrink-0">
                <p className="text-xs text-gray-600 text-center whitespace-nowrap">{timeLabel}</p>
              </div>
              <ActivityViewRowContent
                actor={<ActivityActor activity={lastActivity} hideName={true} />}
                item={item}
                activities={activities}
                currentUser={currentUser}
                fields={fields}
                simple={true}
                className="text-xs"
                navigateToUrl={(appUrl, jiraUrl) => {
                  navigateTo({
                    jiraUrlParams: {
                      relativeUrl: jiraUrl,
                    },
                    appUrl: appUrl,
                    navigate,
                  });
                }}
              />
            </div>
          );
        });
    };

    const renderRow = (l: any) => {
      if (variant === "tasks") {
        // Convert log to TaskRow format - same structure as ItemActivityLogs.tsx
        const summary = l.getIn(["event", "data", "summary"]) || t("Task");
        const timeZone = l.getIn(["event", "data", "timeZone"]);
        let start = l.getIn(["event", "data", "start"]);
        if (typeof start === "number") {
          start = formatInTimeZone(
            start,
            timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "yyyy-MM-dd"
          );
        }
        const startTime = l.getIn(["event", "data", "startTime"]) || "";

        // Compute due label
        const computeDueLabel = (start: any, startTime: string) => {
          if (!start) return "";
          let dt: Date;
          if (typeof start === "number") {
            dt = new Date(start);
          } else if (typeof start === "string") {
            const dtStr = `${start}${startTime ? `T${startTime}` : ""}`;
            dt = new Date(dtStr);
          } else {
            return "";
          }
          if (!isValid(dt)) return "";
          const days = differenceInCalendarDays(dt, new Date());
          if (days === 0) return "@Due today";
          if (days > 0) return `@Due in ${days} day${days > 1 ? "s" : ""}`;
          const ago = Math.abs(days);
          return `@Due ${ago} day${ago > 1 ? "s" : ""} ago`;
        };

        const dueLabel = computeDueLabel(start, startTime);

        // Get assignees
        const assigneesRaw = l.getIn(["event", "data", "assignees"]) || [];
        let assignees: { name?: string; avatarUrl?: string }[] = [];
        try {
          const raw = assigneesRaw?.toJS?.() ?? assigneesRaw;
          const arr = Array.isArray(raw) ? raw : [raw];
          assignees = arr
            .map((acc: any) => {
              const accountId = typeof acc === "string" ? acc : acc?.accountId || acc;
              const member = getMember(members, accountId);
              return {
                name: member.get("displayName"),
                avatarUrl: member.get("avatarUrl"),
              };
            })
            .filter(Boolean);
        } catch {}

        // Get first assignee for display
        const firstAssignee = assignees && assignees.length > 0 ? assignees[0] : null;
        const assigneeName = firstAssignee?.name || "";
        const assigneeAvatar = firstAssignee?.avatarUrl || "";

        return (
          <QuickTaskRow
            key={l.get("id")}
            log={l}
            title={summary}
            completed={l.getIn(["event", "data", "done"]) || false}
            dueLabel={dueLabel}
            assigneeName={assigneeName}
            assigneeAvatar={assigneeAvatar}
            onOpen={() => {
              // Navigate to tasks tab when clicking on task
              onNavigateTab?.(ActivityTabs.TASKS.key);
            }}
            onToggleComplete={(id) => {
              if (!itemId) return;
              const current = l.getIn(["event", "data", "done"]) || false;
              const next = !current;
              markAsDoneActivity(itemId, String(id), next)
                .then(() => {
                  dispatch(
                    ActivityAction.searchActivities({
                      itemId,
                      scope: "item",
                      includeDependencies:
                        contentType === "COMPANY" ||
                        contentType === "CONTACT" ||
                        contentType === "SALE",
                    })
                  );
                  if (isGmailContext()) {
                    dispatch(ActivityAction.searchUpcomingChrome(projectId, upcomingFilters));
                  }
                })
                .catch(() => {
                  // swallow for now
                });
            }}
            onDelete={(id) => {
              setDeleteId(id);
              setDeleteType("task");
              setConfirmOpen(true);
            }}
          />
        );
      }
      if (variant === "notes") {
        const time = formatDistanceToNowActivity(l);
        const summary = l.getIn(["event", "data", "summary"]) || t("Note");

        const personId = l.getIn(["event", "person"]);
        const person = getMember(members, personId);
        const actorName = person?.get("displayName") || "";
        const actorAvatar = person?.get("avatarUrl") || "";

        // Check for deadline detail
        const activityType = l.getIn(["event", "data", "activityType"]);
        const whenLabel = l.getIn(["event", "data", "whenLabel"]);
        let deadlineDetail = "";
        if (activityType === "deadline" && whenLabel) {
          // Format deadline detail like "@Tomorrow"
          deadlineDetail = whenLabel.startsWith("@") ? whenLabel : `@${whenLabel}`;
        }

        return (
          <QuickNotesRow
            key={l.get("id")}
            log={l}
            time={time}
            summary={summary}
            actorName={actorName}
            actorAvatar={actorAvatar}
            deadlineDetail={deadlineDetail}
            onOpen={(id) => {
              dispatch(ActivityAction.showActivityDialog(l.get("itemId"), id));
            }}
            onDelete={(id) => {
              setDeleteId(id);
              setDeleteType("task");
              setConfirmOpen(true);
            }}
          />
        );
      }
      if (variant === "emails") {
        // Convert log to EmailRow format - same structure as ItemActivityLogs.tsx
        const createdAt = l.getIn(["event", "createdAt"]);
        const subject = l.getIn(["event", "data", "summary"]) || t("No Subject");
        const person = getMember(members, l.getIn(["event", "person"]));
        const attendees = l.getIn(["event", "data", "attendees"])?.toJS() || [];
        const uniqueAttendees = [...new Set<string>(attendees)];
        const emailRow: EmailRow = {
          id: l.get("id"),
          subject,
          to: uniqueAttendees.join(", "),
          recipients: uniqueAttendees,
          timeLabel: formatDistanceToNowActivity(l),
          createdAt,
          senderName: person.get("displayName"),
          senderAvatar: person.get("avatarUrl"),
          trackingEnabled: l.getIn(["event", "data", "tracking"]) || false,
          visibility: l.getIn(["event", "data", "visibility"]) || undefined,
          messageId: l.getIn(["event", "data", "messageId"]) || undefined,
          threadId: l.getIn(["event", "data", "threadId"]) || undefined,
          provider: l.getIn(["event", "data", "hasGmail"]) ? "GMAIL" : "OUTLOOK",
        };
        return (
          <EmailRowComponent
            email={emailRow}
            showTimeFirst={true}
            onOpen={() => {
              dispatch(ActivityAction.showActivityDialog(l.get("itemId"), l.get("id")));
            }}
            onTrackingEmail={(messageId, threadId) => {
              dispatch(
                TaskAction.showRightPanelDialog(
                  {
                    messageId,
                    threadId,
                  },
                  DialogTypes.sideMyInboxMessageDialog
                )
              );
            }}
          />
        );
      }
      // Convert log to FileRow format - same structure as ItemActivityLogs.tsx
      const member = getMember(members, l.getIn(["event", "person"]));
      const visibility = l.getIn(["event", "data", "visibility"]) || undefined;
      const fileName = l.getIn(["event", "data", "fileName"]) || t("File");
      const fileSize = l.getIn(["event", "data", "fileSize"]) || "0 bytes";
      const fileContentType = l.getIn(["event", "data", "fileContentType"]) || "application/pdf";

      const fileRow: FileRow = {
        id: l.get("id"),
        visibility,
        name: fileName,
        size: fileSize,
        sizeType: fileContentType,
        uploaderName: member.get("displayName"),
        uploaderAvatar: member.get("avatarUrl"),
        timeLabel: formatDistanceToNowActivity(l),
      };

      return (
        <FileRowComponent
          file={fileRow}
          showTimeFirst={true}
          onOpen={() => {
            const file: any = fileRow;
            if (!file) return;
            const fileName = file.name;
            if (!fileName) return;
            downloadFileLogActivity(fileName).then(openLink);
          }}
          onDeleteClick={(id) => {
            setDeleteId(id);
            setDeleteType("file");
            setConfirmOpen(true);
          }}
        />
      );
    };

    return (
      <div className="rounded-[12px] border border-gray-200 bg-white shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)] mb-5">
        <div className="flex items-center justify-between p-3 pt-2">
          <div
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50 rounded-lg pt-1"
            onClick={() => {
              const key = mapTitleToTabKey(title);
              if (key) onNavigateTab?.(key);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                const key = mapTitleToTabKey(title);
                if (key) onNavigateTab?.(key);
              }
            }}
          >
            {icon}
            <span>{t(title)}</span>
            <ChevronRightIcon className="w-4 h-4 text-gray-800" />
          </div>
          <div className="pt-1">{action}</div>
        </div>
        <div className="p-3 space-y-2">
          {items.length === 0 ? (
            <div className="text-xs text-gray-500">{t("No data")}</div>
          ) : (
            <>
              {variant === "activity"
                ? renderActivity()
                : items.map((l: any) => (
                    <React.Fragment key={l.get("id") || Math.random()}>
                      {renderRow(l)}
                    </React.Fragment>
                  ))}
            </>
          )}
        </div>
      </div>
    );
  };

  const handleActivityMeeting = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.meeting,
        summary: "Meeting",
      })
    );
  };

  const handleActivityCall = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.call,
        summary: "Call",
      })
    );
  };

  const handleActivityDeadline = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.deadline,
        summary: "Deadline",
      })
    );
  };

  const handleAddTask = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.task,
        summary: "",
      })
    );
  };

  const handleAddNote = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.commented,
        summary: "Note",
      })
    );
  };

  const handleLogEmail = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.logEmail,
        summary: "",
      })
    );
  };

  const handleComposeEmail = () => {
    const primaryContactId =
      item.getIn(["data", "contact", "value", "id"]) ||
      item.getIn(["data", "contact", "value", "value"]);
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        attendees: item.getIn(["data", "email", "value"])
          ? [item.getIn(["data", "email", "value"])]
          : [],
        contactId: primaryContactId,
        activityType: ActivityTypes.sendEmail,
        summary: "",
      })
    );
  };

  const handleUploadFile = () => {
    dispatch(
      ActivityAction.showActivityDialog(item.get("id"), null, {
        item: {
          value: item.get("id"),
          label: item.getIn(["data", "name", "value"]),
          type: item.get("type"),
        },
        activityType: ActivityTypes.file,
      })
    );
  };

  // Determine if this is Account type
  const isAccountType = contentType === "COMPANY";
  const isSaleType = contentType === "SALE";
  const isContactType = contentType === "CONTACT";

  // Calculate loading state (similar to DealDetailPanel)
  // Note: activityLoading (pending) is not used because it tracks activity actions, not itemLogs fetching
  const calculatedLoading = taskLoading || bucketsLoading || isLoading;

  // Show loading state
  if (calculatedLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <ChartLoading label="Loading..." />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Row 1 */}
      <div
        className={`grid gap-3 grid-cols-1 ${
          isContactType ? "md:grid-cols-2" : "md:grid-cols-2 xl:grid-cols-4"
        }`}
      >
        {/* Funnel Stage - Only for SALE type */}
        {isSaleType && funnelStage.totalStages > 0 && (
          <FunnelStageCard
            currentStage={funnelStage.currentStage}
            stageName={funnelStage.stageName}
            totalStages={funnelStage.totalStages}
            stages={funnelStage.stages}
            onSelectStage={onSelectStage}
          />
        )}

        {/* Connection Strength */}
        <div className="rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-1 mb-2">
            <Zap className="w-4 h-4 text-gray-600" />
            <div className="text-xs font-medium text-gray-600">{t("Connection Strength")}</div>
          </div>
          <div className="mb-2">
            <div
              className="flex space-x-2 items-center px-2 py-1 text-xs font-medium text-gray-600 w-max rounded-full"
              style={{ backgroundColor: hexToRgba(connectionStrength.get("color")) }}
            >
              <span
                style={{ backgroundColor: connectionStrength.get("color") }}
                className="w-2 h-2 rounded-full"
              ></span>
              <span style={{ color: connectionStrength.get("color") }}>
                {connectionStrength.get("text")}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CornerDownRight className="w-4 h-4 text-gray-600" />
            <div className="text-xs text-gray-600">
              {connectionsCount} {t("connections")}
            </div>
          </div>
        </div>

        {/* Estimated ARR - For Account type, Estimated Revenue - For Sale type */}
        {isAccountType ? (
          <div className="rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-1 mb-2">
              <BadgeDollarSign className="w-4 h-4 text-gray-600" />
              <div className="text-xs font-medium text-gray-600">{t("Estimated ARR")}</div>
            </div>
            <div className="text-xs font-semibold text-gray-900">
              {estimatedArr ? formatAmount(i18n.language, estimatedArr, currency, "0$") : t("N/A")}
            </div>
          </div>
        ) : (
          /* Customer Connection - Only for SALE type */
          <ConnectionCard
            title={t("Customer Connection")}
            itemId={
              item.getIn(["data", "contact", "value", "id"]) ||
              item.getIn(["data", "contact", "value", "value"])
            }
          />
        )}

        {/* Team - Only for Account type */}
        {isAccountType ? (
          <div className="rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-1 mb-2">
              <Users className="w-4 h-4 text-gray-600" />
              <div className="text-xs font-medium text-gray-600">{t("Team")}</div>
            </div>
            <div className="flex flex-col gap-1.5">
              {teamMembers.length > 0 ? (
                teamMembers.map((member: any, index: number) => (
                  <div key={member.id || index} className="flex items-center gap-2">
                    {member.avatarUrl ? (
                      <img
                        src={member.avatarUrl}
                        alt=""
                        className="w-5 h-5 rounded-full shrink-0"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <DefaultAvatar
                        name={member.name}
                        size="sm"
                        className="w-5 h-5 rounded-full shrink-0"
                      />
                    )}
                    <div className="text-sm font-medium text-gray-900 truncate">{member.name}</div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">{t("No team members")}</div>
              )}
            </div>
          </div>
        ) : null}

        {/* Funding raised - Only for Account type */}
        {isAccountType && (
          <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-1 mb-2">
              <BadgeDollarSign className="w-4 h-4 text-gray-600" />
              <div className="text-xs font-medium text-gray-600">{t("Funding raised")}</div>
            </div>
            <div></div>
            <div className="text-xs font-semibold text-gray-900">
              {fundingRaised
                ? formatAmount(i18n.language, fundingRaised, currency, "N/A")
                : t("$0")}
            </div>
          </div>
        )}

        {/* Predictability */}
        {isSaleType && (
          <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-1 mb-2">
              <BadgePercent className="w-4 h-4 text-gray-600" />
              <div className="text-xs font-medium text-gray-600">{t("Predictability")}</div>
            </div>
            <div></div>
            <div className="text-base font-semibold text-gray-900">
              {Math.round(predictPercent)}%
            </div>
          </div>
        )}
      </div>

      {/* Row 2 */}
      <div
        className={`grid gap-3 grid-cols-1 ${
          isContactType ? "md:grid-cols-3" : "md:grid-cols-2 xl:grid-cols-4"
        }`}
      >
        {/* Employee range - Only for Account type */}
        {isAccountType && (
          <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-1 mb-2">
              <Users className="w-4 h-4 text-gray-600" />
              <div className="text-xs font-medium text-gray-600">{t("Employee range")}</div>
            </div>
            <div></div>
            <div className="text-xs font-semibold text-gray-900">{employeeRange || t("N/A")}</div>
          </div>
        )}

        {/* Estimated Revenue - Only for SALE type */}
        {isSaleType && (
          <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
            <div className="flex items-center gap-1 mb-2">
              <BadgePercent className="w-4 h-4 text-gray-600" />
              <div className="text-xs font-medium text-gray-600">{t("Estimated Revenue")}</div>
            </div>
            <div></div>
            <div className="text-base font-semibold text-gray-900">
              {formatAmount(i18n.language, estimatedRevenue, currency, "0$")}
            </div>
          </div>
        )}

        {/* Next Due Task */}
        <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-1 mb-2">
            <CheckSquareIcon className="w-4 h-4 text-gray-600" />
            <div className="text-xs font-medium text-gray-600">{t("Next Due Task")}</div>
          </div>
          <div className="text-sm font-medium text-gray-900 truncate">
            {upcomingTask?.summary || t("No upcoming tasks")}
          </div>
          {nextDueTaskLabel ? (
            <div className="flex items-center gap-2">
              <CornerDownRight className="w-4 h-4 text-gray-600" />
              <div
                className={`mt-1 text-xs ${
                  nextDueTaskLabel.includes("today") ? "text-yellow-600" : "text-gray-600"
                }`}
              >
                {nextDueTaskLabel}
              </div>
            </div>
          ) : (
            <div></div>
          )}
        </div>

        {/* Next Meeting */}
        <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-1 mb-2">
            <CalendarCheck className="w-4 h-4 text-gray-600" />
            <div className="text-xs font-medium text-gray-600">{t("Next Meeting")}</div>
          </div>
          <div className="text-sm font-medium text-gray-900 truncate">
            {upcomingMeeting?.summary || t("No upcoming meetings")}
          </div>
          {nextMeetingLabel ? (
            <div className="flex items-center gap-2">
              <CornerDownRight className="w-4 h-4 text-gray-600" />
              <div className="mt-1 text-xs text-gray-600">{nextMeetingLabel}</div>
            </div>
          ) : (
            <div></div>
          )}
        </div>

        {/* Last Interaction */}
        <div className="grid grid-flow-col grid-rows-3 rounded-[12px] border border-gray-200 p-2 bg-gray-50 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-1 mb-2">
            <CalendarClock className="w-4 h-4 text-gray-600" />
            <div className="text-xs font-medium text-gray-600">{t("Last Interaction")}</div>
          </div>
          <div className="text-sm font-medium text-gray-900 truncate">
            {lastInteraction?.getIn(["event", "data", "summary"]) || t("No recent interactions")}
          </div>
          {lastInteractionText ? (
            <div className="flex items-center gap-2">
              <CornerDownRight className="w-4 h-4 text-gray-600" />
              <div className="mt-1 text-xs text-gray-600">{lastInteractionText}</div>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>

      {/* Quick View sections - per Figma */}
      <div className="flex-col gap-3">
        <QuickSection
          title="Activity"
          icon={<Activity className="w-5 h-5" />}
          action={
            <Menu as="div" className="relative inline-block shrink-0">
              <MenuButton className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer outline-none">
                <PlusIcon className="w-3 h-3" />
                <span>{t("Add Activity")}</span>
              </MenuButton>
              <MenuItems className="absolute left-0 z-20 py-2 origin-top-left bg-white shadow-xl rounded ring-opacity-5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-leave:duration-75 data-enter:ease-out data-leave:ease-in">
                <MenuItem>
                  <button
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                    onClick={handleActivityMeeting}
                  >
                    <CalendarDays className="w-3 h-3" />
                    <span>{t("Meeting")}</span>
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                    onClick={handleActivityCall}
                  >
                    <Phone className="w-3 h-3" />
                    <span>{t("Call")}</span>
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100"
                    onClick={handleActivityDeadline}
                  >
                    <Flag className="w-3 h-3" />
                    <span>{t("Deadline")}</span>
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          }
          items={recentActivity}
          variant="activity"
          onNavigateTab={onNavigateTab}
        />
        <QuickSection
          title="Tasks"
          icon={<CheckSquareIcon className="w-5 h-5" />}
          action={
            <div
              className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer"
              onClick={handleAddTask}
            >
              <PlusIcon className="w-3 h-3" />
              <span>{t("Add Task")}</span>
            </div>
          }
          items={recentTasks}
          variant="tasks"
          onNavigateTab={onNavigateTab}
        />
        <QuickSection
          title="Notes"
          icon={<PencilSquareIcon className="w-5 h-5" />}
          action={
            <div
              className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer"
              onClick={handleAddNote}
            >
              <PlusIcon className="w-3 h-3" />
              <span>{t("Add Note")}</span>
            </div>
          }
          items={recentNotes}
          variant="notes"
          onNavigateTab={onNavigateTab}
        />
        <QuickSection
          title="Emails"
          icon={<InboxIcon className="w-5 h-5" />}
          action={
            <div className="flex items-center gap-2">
              <div
                className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer"
                onClick={handleLogEmail}
              >
                <MailPlus className="w-3 h-3" />
                <span>{t("Log Email")}</span>
              </div>
              <div
                className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer"
                onClick={handleComposeEmail}
              >
                {renderIconComposeEmail()}
                <span>{t("Compose Email")}</span>
              </div>
            </div>
          }
          items={recentEmails}
          variant="emails"
          onNavigateTab={onNavigateTab}
        />
        <QuickSection
          title="Files"
          icon={<FolderOpenIcon className="w-5 h-5" />}
          action={
            <div
              className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded border border-gray-200 shadow-sm cursor-pointer"
              onClick={handleUploadFile}
            >
              <PlusIcon className="w-3 h-3" />
              <span>{t("Upload File")}</span>
            </div>
          }
          items={recentFiles}
          variant="files"
          onNavigateTab={onNavigateTab}
        />
      </div>

      <ConfirmDialog
        visible={confirmOpen}
        title={deleteType === "file" ? t("Delete file?") : t("Delete task?")}
        description={
          deleteType === "file"
            ? t("This action cannot be undone. The file will be permanently deleted.")
            : t("This action cannot be undone. The task will be permanently deleted.")
        }
        alertIcon={<OctagonAlertIcon />}
        dangerBtn
        submitting={deleting}
        onClose={() => {
          if (!deleting) {
            setConfirmOpen(false);
            setDeleteId(null);
            setDeleteType(null);
          }
        }}
        onSubmit={async () => {
          if (!deleteId || !itemId) return;
          setDeleting(true);
          try {
            await deleteItemLog(itemId, deleteId as string);
            dispatch(
              ActivityAction.searchActivities({
                itemId,
                scope: "item",
                includeDependencies:
                  contentType === "COMPANY" || contentType === "CONTACT" || contentType === "SALE",
              })
            );
            setConfirmOpen(false);
            setDeleteId(null);
            setDeleteType(null);
          } catch {
            // Error handling if needed
          } finally {
            setDeleting(false);
          }
        }}
        submitBtnLabel={t("Delete")}
      />
    </div>
  );
};

export default ItemOverview;
